type Lang = 'id' | 'en' | 'zh'

const HUMAN_MESSAGES: Record<Lang, Record<string, string>> = {
  id: {
    generic: 'Maaf, terjadi kendala. Silakan coba lagi.',
    unauthorized: 'Sesi Anda berakhir. Silakan login kembali.',
    invalid: 'Data yang Anda masukkan belum lengkap atau belum sesuai.',
    notFound: 'Data yang Anda cari tidak ditemukan.',
    conflict: 'Data ini sudah ada. Silakan gunakan data yang berbeda.',
    network: 'Koneksi terputus. Periksa internet Anda lalu coba lagi.',
    server: 'Sistem sedang sibuk. Silakan coba lagi sebentar.',
  },
  en: {
    generic: 'Something went wrong. Please try again.',
    unauthorized: 'Your session has expired. Please sign in again.',
    invalid: 'Some information is missing or invalid. Please check and try again.',
    notFound: 'We couldn\'t find the requested data.',
    conflict: 'This data already exists. Please use a different value.',
    network: 'Connection lost. Please check your internet and try again.',
    server: 'The system is busy right now. Please try again in a moment.',
  },
  zh: {
    generic: '抱歉，系统出现了问题，请稍后再试。',
    unauthorized: '登录状态已过期，请重新登录。',
    invalid: '您输入的信息不完整或格式不正确，请检查后重试。',
    notFound: '未找到您要查看的数据。',
    conflict: '该数据已存在，请使用其他内容。',
    network: '网络连接异常，请检查后重试。',
    server: '系统繁忙，请稍后再试。',
  },
}

function getCurrentLang(): Lang {
  const lang = (localStorage.getItem('lang') || 'en').toLowerCase()
  if (lang.startsWith('zh')) return 'zh'
  if (lang.startsWith('id')) return 'id'
  return 'en'
}

export function humanizeErrorMessage(rawMessage: string, statusCode?: number): string {
  const message = (rawMessage || '').trim()
  const lower = message.toLowerCase()
  const text = HUMAN_MESSAGES[getCurrentLang()]

  if (!message) return text.generic

  if (
    statusCode === 401 ||
    lower.includes('unauthorized') ||
    lower.includes('no auth') ||
    lower.includes('forbidden') ||
    lower.includes('token expired') ||
    lower.includes('session expired')
  ) {
    return text.unauthorized
  }

  if (
    lower.includes('invalid parameter') ||
    lower.includes('invalid parameters') ||
    lower.includes('invalid id') ||
    lower.includes('validation') ||
    lower.includes('required') ||
    lower.includes('captcha') ||
    message.includes('参数') ||
    message.includes('验证码') ||
    message.includes('必填') ||
    message.includes('格式')
  ) {
    return text.invalid
  }

  if (
    lower.includes('not found') ||
    lower.includes('does not exist') ||
    message.includes('未找到') ||
    message.includes('不存在') ||
    statusCode === 404
  ) {
    return text.notFound
  }

  if (
    lower.includes('already exists') ||
    lower.includes('duplicate') ||
    lower.includes('conflict') ||
    statusCode === 409
  ) {
    return text.conflict
  }

  if (
    lower.includes('network') ||
    lower.includes('failed to fetch') ||
    lower.includes('timeout') ||
    lower.includes('connection') ||
    lower.includes('econnrefused')
  ) {
    return text.network
  }

  if (
    lower.includes('server error') ||
    lower.includes('internal error') ||
    lower.includes('query failed') ||
    lower.includes('database') ||
    lower.includes('sql') ||
    lower.includes('gorm') ||
    lower.includes('panic') ||
    (statusCode !== undefined && statusCode >= 500)
  ) {
    return text.server
  }

  if (
    lower.startsWith('failed to ') ||
    lower.includes('stack trace') ||
    lower.includes('exception') ||
    lower.includes('runtime error')
  ) {
    return text.generic
  }

  const looksTechnical = [
    'sql',
    'mysql',
    'gorm',
    'syntax error',
    'panic',
    'nil pointer',
    'unmarshal',
    'marshal',
    'json:',
    'strconv',
    'parse error',
    'bind error',
    'eof',
    'stack',
    'trace',
    'internal',
    'database',
  ].some(keyword => lower.includes(keyword))

  if (looksTechnical) {
    return (statusCode !== undefined && statusCode >= 500) ? text.server : text.generic
  }

  // For any unrecognized non-2xx error, keep message user-friendly.
  if (statusCode !== undefined && statusCode >= 400) {
    return statusCode >= 500 ? text.server : text.generic
  }

  return message
}
